import {QueryClient} from '@tanstack/react-query'
import escapeHtml from 'escape-html'
import {BlockName, getBlock, getPalettes} from 'quickstart'
import {createStore} from 'quickstart/store'
import {AdminContextFromServer, BlockContext} from 'quickstart/types'
import {logger, setDebug, tryJson} from 'tizra'
import {TabStructureContent, tabStructure} from './tab-structure'

const log = logger('entry-client')

const renderBlockAdmin = ({
  adminName = 'admin',
  blockName,
  context,
  heading,
  queryClient = new QueryClient(),
  root,
  wrapper,
  textarea = wrapper?.getElementsByTagName(
    'textarea',
  )[0] as HTMLTextAreaElement,
}: {
  blockName: BlockName
  root: HTMLElement
  textarea?: HTMLTextAreaElement
  context: AdminContextFromServer
  adminName?: 'admin' | 'globalAdmin'
  heading?: HTMLElement
  queryClient?: QueryClient
  wrapper?: HTMLElement
}) => {
  const fail = (...args: any) => {
    if (args.length) {
      log.error(...args)
    }
    // Since we couldn't render the react app, display the JSON config
    // instead.
    wrapper?.style.removeProperty('display')
  }

  // Look up the Quickstart block by name. This can fail for non-Quickstart
  // templates, or for blocks that aren't supported by the current Quickstart
  // template version.
  const qb = getBlock(blockName)
  if (!qb) {
    return fail(`Failed getBlock ${blockName}`)
  }

  // If we were passed a heading element, update it with the block title.
  if (heading) {
    heading.innerText = qb.title
  }

  // Attempt to render the Quickstart block admin form on the root node.
  // It will record changes back to the textarea containing the JSON
  // config.
  let cleanup: () => void
  try {
    cleanup = qb[adminName]!({root, textarea, context, queryClient})
  } catch (e) {
    return fail(`Failed to render ${adminName} for ${blockName}`, e)
  }

  // There are two situations in which we need to clean up the react app:
  //  - when the form is submitted
  //  - when the form is cancelled
  //
  // On form submit, we need to clean up the app proactively, to avoid including
  // react-rendered fields in the submitted form.
  //
  // On form cancel, when the dialog closes without submitting the form, we
  // clean up lazily, whenever the root node is removed from the DOM. In
  // practice this doesn't happen until the dialog opens again, or the user
  // navigates to a different layout or admin screen. But that's fine, the app
  // isn't hurting anything, we just need to clean up to avoid a memory leak.

  // Clean up on submit.
  const form = textarea.closest('form') as HTMLFormElement
  const wrappedOnsubmit = form.onsubmit
  form.onsubmit = function (...args) {
    cleanup()
    if (wrappedOnsubmit) {
      return wrappedOnsubmit.apply(this, args)
    }
  }

  // Clean up on cancel.
  const observer = new MutationObserver(function (mutationsList) {
    for (const m of mutationsList) {
      if (Array.from(m.removedNodes).includes(root)) {
        cleanup()
      }
    }
  })
  observer.observe(root.parentNode as Node, {
    attributes: false,
    childList: true, // watching for nodes added and removed
    subtree: false, // since observing parent, instead of document.body
  })
}

const renderDesignTabContent = (props: {
  content: TabStructureContent
  context: AdminContextFromServer
  form: HTMLFormElement
  queryClient: QueryClient
  wrapper: HTMLElement
}) => {
  log.log('renderDesignTabContent', props)
  const {
    content,
    content: {contents = [], globalAdmin, params, toggle},
    context,
    form,
    queryClient,
    wrapper,
  } = props
  if (toggle) {
    const [first, second] = ['tbody', 'tbody'].map(tagName =>
      wrapper.appendChild(document.createElement(tagName)),
    )
    first.innerHTML = `
        <tr>
          <td colspan="3">
            <h2>
              <a href="#">
                ${escapeHtml(toggle)}
              </a>
            </h2>
          </td>
        </tr>
      `
    // @ts-expect-error
    first.querySelector('a').onclick = e => {
      e.preventDefault()
      second.style.display = second.style.display === 'none' ? '' : 'none'
    }
    contents.forEach(content =>
      renderDesignTabContent({
        content,
        context,
        form,
        queryClient,
        wrapper: second,
      }),
    )
    return
  } else if (globalAdmin) {
    const row = document.createElement('tr')
    row.innerHTML = `
      <td colspan="2"></td>
      <td></td>
    `
    if (wrapper.tagName === 'TBODY') {
      wrapper.appendChild(row)
    } else {
      const tbody = document.createElement('tbody')
      tbody.appendChild(row)
      wrapper.appendChild(tbody)
    }
    log.log({row})
    renderBlockAdmin({
      adminName: 'globalAdmin',
      blockName: globalAdmin,
      context,
      queryClient,
      root: row.querySelector('td') as HTMLTableCellElement,
      textarea: form.querySelector(
        'tr[data-param-name="QuickstartGlobalConfig"] textarea',
      ) as HTMLTextAreaElement,
    })
  } else if (params) {
    const tbody =
      wrapper.tagName === 'TBODY'
        ? wrapper
        : wrapper.appendChild(document.createElement('tbody'))
    const rows = Array.from(
      form.querySelectorAll<HTMLTableRowElement>('tr[data-param-name]'),
    ).filter(
      ({dataset: {paramName}}) => paramName && params.includes(paramName),
    )
    rows.forEach(row => {
      tbody.appendChild(row)
      row.style.display = ''
    })
  } else {
    log.warn('unknown renderDesignTab content:', content)
  }
}

const renderDesignTab = (props: {
  context: AdminContextFromServer
  form: HTMLFormElement
  viewId: keyof typeof tabStructure
}) => {
  log.log('renderDesignTab', props)
  const {context, form, viewId} = props
  const ts = tabStructure[viewId]
  if (!ts) {
    alert(`Quickstart.js doesn't know viewId: ${viewId}`)
    return
  }

  // Create the react-query client for API calls, shared by global admins on
  // this design tab.
  const queryClient = new QueryClient()

  ts.contents.forEach(content =>
    renderDesignTabContent({
      content,
      context,
      form,
      queryClient,
      wrapper: form.querySelector('table') as HTMLTableElement,
    }),
  )
}

const renderBlocks = ({blockContextJson}: {blockContextJson: string}) => {
  const context =
    tryJson<BlockContext>(blockContextJson, e =>
      log.error('Failed to parse blockContextJson', e),
    ) || ({} as BlockContext)

  // Create the redux store for intra-block communication.
  const store = createStore()

  // Create the react-query client for API calls, shared by blocks on the page.
  const queryClient = new QueryClient()

  // Render the global block to a div at the end of body.
  try {
    const root = document.createElement('div')
    root.className = 'quickstart-global'
    document.body.appendChild(root)
    getBlock('global').render({
      blockId: 'global',
      config: null,
      context,
      hydrate: false,
      queryClient,
      root,
      sortKey: 0,
      store,
    })
  } catch (e) {
    log.error('Failed to render Quickstart GlobalBlock', e)
  }

  // Render the theme block to a div at the end of body. Two things happen here:
  //
  // 1. The div is rendered with the content of ThemeBlock, which is probably
  //    just a <link> for the Google Font. This tag is redundant if SSR is
  //    enabled, because the ThemeBlock also renders in <head>, but it picks up
  //    the slack if SSR is disabled (by setting disable-ssr in QuickstartHacks)
  //
  // 2. ThemeBlock also renders <GlobalStyles>, which doesn't appear in the div.
  //    If SSR is enabled, rendering <GlobalStyles> will take over styling from
  //    the <style> tag in <head>
  //
  try {
    // Make a root div for rendering at the end of body. Previously, before the
    // ThemeBlock rendered the <link> tag, this was just a disconnected div. Now
    // that it renders a <link>, it needs to be appended to body.
    const root = document.createElement('div')
    root.className = 'quickstart-theme'
    document.body.appendChild(root)
    getBlock('theme').render({
      blockId: 'theme',
      config: null,
      context,
      hydrate: false,
      queryClient,
      root,
      sortKey: 0,
      store,
    })
  } catch (e) {
    log.error('Failed to render Quickstart ThemeBlock', e)
  }

  // Render each block. These will hydrate if they were rendered on the server
  // side first.
  document
    .querySelectorAll<HTMLElement>('.t-quickstart-block > div[data-block-id]')
    .forEach((root, index) => {
      const blockId = root.dataset.blockId!
      const blockName = root.dataset.blockName as BlockName
      const blockConfig = tryJson(root.dataset.blockConfig, e =>
        log.error(
          `Failed to parse config for Quickstart block ${blockName}`,
          e,
        ),
      )
      try {
        const block = getBlock(blockName!)
        block.render({
          blockId,
          config: blockConfig,
          context,
          hydrate: root.hasChildNodes(),
          queryClient,
          root,
          sortKey: index + 1,
          store,
        })
      } catch (e) {
        log.error(`Failed to render Quickstart block ${blockName}`, e)
      }
    })
}

// @ts-expect-error window.tizra
Object.assign((window.tizra ||= {}), {
  quickstart: {
    getBlock,
    getPalettes,
    renderBlockAdmin,
    renderBlocks,
    renderDesignTab,
  },
  setDebug,
})
